import { isEmpty, isNumber } from 'lodash-es';
import { useMemo, type JSX } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { MultiMeterBarChart } from '../../../../charts/meter-bar/multi-meter-bar/MultiMeterBarChart.js';
import { METER_BAR_SIZE } from '../../../../charts/meter-bar/shared/constants.js';
import { MeterBarChart } from '../../../../charts/meter-bar/single-meter-bar/MeterBarChart.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import { isDisplayColumnDef } from '../../hooks/useTable/datatablev2-typeguards.js';
import type { Column, TableOptions } from '../../hooks/useTable/types.js';
import type {
  DataTableV2ColumnDef,
  DataTableV2DisplayColumnDef,
  DataTableV2RowData,
} from '../../public.api.js';
import type { DataTableV2MeterbarColumnConfig } from '../ColumnTypes/ColumnTypes.js';

/**
 * Extends the tanstack table's state for the MeterBar chart.
 * @internal
 */
export interface DataTableV2MeterbarChartState {
  /**
   * Object to store the highest total max values for all meterbar columns
   */
  meterbarChartMaxValues?: Record<string, number>;
}

/**
 * Typeguard function ensures that the defined config in the column definition contains certain properties.
 * @internal
 */
function isDataTableV2MeterBarColumnConfig(
  config: unknown,
): config is DataTableV2MeterbarColumnConfig {
  return (
    config !== null &&
    config !== undefined &&
    typeof config === 'object' &&
    ('max' in config ||
      'color' in config ||
      'threshold' in config ||
      'showTooltip' in config ||
      'max' in config)
  );
}

/**
 * Typeguard function ensures that the defined column contains the 'accessor' property.
 * @internal
 */
function isColumnDefWithAccessor<TData, TValue>(
  column: unknown,
): column is DataTableV2DisplayColumnDef<TData, TValue> {
  return (
    column !== null &&
    column !== undefined &&
    typeof column === 'object' &&
    'accessor' in column
  );
}

type MeterBarChartRendererType<TData, TValue> = {
  value: number | { name: string; value: number; color?: string }[];
  column: Column<TData, TValue>;
};

const DEFAULT_MAX = 100;

/**
 * Intended for configuring and rendering the meterbar chart component.
 */
export function MeterBarChartRenderer<TData, TValue>({
  value,
  column,
}: MeterBarChartRendererType<TData, TValue>): JSX.Element {
  const table = useDataTableV2();
  let config: DataTableV2MeterbarColumnConfig = {};
  if (
    column?.columnDef.meta &&
    isDataTableV2MeterBarColumnConfig(column.columnDef.meta.config)
  ) {
    config = column.columnDef.meta.config;
  }

  const columnId = column.columnDef.id ?? '';

  const maxValuesFromState = table.getState().meterbarChartMaxValues;

  const maxValue = !isEmpty(maxValuesFromState)
    ? maxValuesFromState[columnId]
    : DEFAULT_MAX;

  // simple MeterBar
  if (typeof value === 'number') {
    return (
      <MeterBarChart
        value={value}
        min={config?.min}
        max={maxValue}
        color={config?.color as string}
        size={METER_BAR_SIZE.Size8}
        style={{
          overflow: 'hidden',
          width: '100%',
          pointerEvents: 'auto',
          padding: `${Spacings.Size6} 0`,
        }}
      >
        {config?.showTooltip && <MeterBarChart.Tooltip />}
        {config?.thresholds &&
          config?.thresholds?.map((threshold) => (
            <MeterBarChart.Threshold
              key={`threshold-${threshold.color}-${threshold.value}`}
              name={threshold.name}
              color={threshold.color}
              value={threshold.value}
            />
          ))}
      </MeterBarChart>
    );
  } else {
    const isMultiMeterData =
      Array.isArray(value) &&
      value.every(
        (item) =>
          typeof item === 'object' &&
          typeof item.name === 'string' &&
          typeof item.value === 'number' &&
          (typeof item.color === 'undefined' || typeof item.color === 'string'),
      );

    if (!isMultiMeterData) {
      // eslint-disable-next-line no-console
      console.warn('Invalid meter chart data.');
    }

    return (
      <MultiMeterBarChart
        size={'size8'}
        min={config?.min}
        max={maxValue}
        colorPalette={config?.colorPalette || 'categorical'}
        style={{
          overflow: 'hidden',
          width: '100%',
          pointerEvents: 'auto',
          padding: `${Spacings.Size6} 0`,
        }}
      >
        {
          // only if this is multi meter data render segments
          isMultiMeterData &&
            value.map((data) => (
              <MultiMeterBarChart.Segment
                key={`${data.name}-${data.value}`}
                name={data.name}
                value={data.value}
                color={data?.color}
              />
            ))
        }
        <MeterBarChart.Tooltip />
        {config?.showTooltip && (
          <MultiMeterBarChart.Tooltip variant={'shared'} />
        )}
      </MultiMeterBarChart>
    );
  }
}

/**
 * Gets the highest total max values in a meterbar column.
 *
 * @param data - table data
 * @param columns - table column definition
 * @returns record of highest total max value per column
 * @internal
 */
export function getTotalMaxFromMeterbarChartData<
  TData extends DataTableV2RowData,
>(
  data: TData[],
  columns: DataTableV2ColumnDef<TData>[],
): Record<string, number> {
  const meterbarColumns = columns.filter(
    (columnDef) =>
      isDisplayColumnDef(columnDef) && columnDef.columnType === 'meterbar',
  );

  const maxValuesStorage: Record<string, number> = {};

  for (const row of data) {
    for (const columnDef of meterbarColumns) {
      if (isColumnDefWithAccessor(columnDef)) {
        const { accessor, config, id: columnId } = columnDef;

        const configuredMax: number | 'data-max' | undefined =
          isDataTableV2MeterBarColumnConfig(config) ? config.max : undefined;

        const meterbarValue = row as Record<string, unknown>;

        const meterbarDataEntry =
          typeof accessor === 'string'
            ? meterbarValue[accessor]
            : accessor(row);

        let totalValue = DEFAULT_MAX;

        if (isNumber(configuredMax)) {
          maxValuesStorage[columnId] = configuredMax;
          continue;
        }

        if (meterbarDataEntry) {
          if (Array.isArray(meterbarDataEntry)) {
            totalValue = meterbarDataEntry.reduce(
              (acc, current) => acc + current.value,
              0,
            );
          } else {
            const entry = isNumber(meterbarDataEntry)
              ? meterbarDataEntry
              : DEFAULT_MAX;
            totalValue =
              configuredMax === 'data-max'
                ? entry
                : Math.max(entry, DEFAULT_MAX);
          }
        }

        maxValuesStorage[columnId] = Math.max(
          maxValuesStorage[columnId] ?? totalValue,
          totalValue,
        );
      }
    }
  }

  return maxValuesStorage;
}

/**
 * Configuration hook for the DataTableV2 Meterbar Chart feature.
 * @internal
 */
export function useMeterBarChartMaxValuesCalc<TData extends DataTableV2RowData>(
  props: {
    data: TData[];
    columns: DataTableV2ColumnDef<TData>[];
  },
  options: TableOptions<TData>,
) {
  const meterbarChartMaxValues = useMemo(
    () => getTotalMaxFromMeterbarChartData<TData>(props.data, props.columns),
    [props.columns, props.data],
  );

  options.state = { ...options.state, meterbarChartMaxValues };
}
